import { InfoOutlined } from '@mui/icons-material'
import { Paper, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { LineupApiResponse } from '../postContest/queries/queryTypes'
import { useSelector } from 'react-redux'
import { LateSwapLineup } from '../lateSwap/store/types'

const DataPoint = ({ label, min, max, avg }: { label: string, max: string, min: string, avg: string }) => {
  return (
    <Paper elevation={4} sx={{ borderRadius: 1, backgroundColor: '#18191A', color: 'white' }}>
      <Stack direction='column' alignItems='center' columnGap={2} padding={(theme) => theme.spacing(1, 2)} flexWrap='wrap'>
        <Stack direction='row' alignItems='flex-start' columnGap={2} justifyContent='space-between'>
          <Typography variant='caption' sx={{ fontSize: '16px', fontWeight: '900', maxWidth: '84px' }}>{label}</Typography>
          <Stack alignItems='center'>
            <Typography variant='caption' sx={{ fontSize: '16px' }}>{min}</Typography>
            <Typography variant='caption' sx={{ color: 'rgba(255, 255, 255, .70)' }}>Min</Typography>
          </Stack>
          <Stack alignItems='center'>
            <Typography variant='caption' sx={{ fontSize: '16px' }}>{avg}</Typography>
            <Typography variant='caption' sx={{ color: 'rgba(255, 255, 255, .70)' }}>Avg</Typography>
          </Stack>
          <Stack alignItems='center'>
            <Typography variant='caption' sx={{ fontSize: '16px' }}>{max}</Typography>
            <Typography variant='caption' sx={{ color: 'rgba(255, 255, 255, .70)' }}>Max</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  )
}

export const LateSwapOverview = ({ lateSwapLineups } : { lateSwapLineups: LateSwapLineup[]}) => {
  const [savedLineups, setSavedLineups] = useLocalStorage<LateSwapLineup[]>(
      "lateswap-lineups",
      []
    );
const simRoiData = (lineups: LateSwapLineup[]) => {
    const sorted = [...lineups].sort((a, b) => a.simLineupRoi - b.simLineupRoi);
    const min = sorted[0]?.simLineupRoi * 100 || 0
    const max = sorted[sorted.length - 1]?.simLineupRoi * 100 || 0
    const avg = lineups?.reduce((acc, curr) => acc + curr.simLineupRoi * 100, 0) / lineups.length || 0

    return {
      min: min ? min.toFixed(1) : '-',
      avg: avg ? avg.toFixed(1) : '-',
      max: max ? max.toFixed(1) : '-',
    }
  }
  const data = [
    {
      label: 'Pre Swap Sim ROI',
      ...simRoiData(lateSwapLineups)
    },
    {
      label: 'Post Swap Sim ROI',
      ...simRoiData(savedLineups)
    },
  ]

  return (
    <Stack direction='row' alignItems='center' columnGap={1}>
      <Stack direction='row' alignItems={'center'} columnGap={1}>
        {
          data.map((d, i) => (
            <DataPoint key={i} label={d.label} max={d.max.toString()} min={d.min.toString()} avg={d.avg.toString()} />
          ))
        }
      </Stack>
    </Stack>
  )

}

const LineupPortfolioOverview = () => {
  const { selectedSlate } = useSelector((state: any) => state.common);
  const [savedLineups, setSavedLineups] = useLocalStorage<LineupApiResponse[]>(
    `precontest-lineups-${selectedSlate.slateId}`,
    []
  );

  const toolTipText = 'Aggregate metrics for your favorited lineups. Diversify your lineups through uniques and exposure caps while maintaining strong Sim ROI and Projected FP values.'
  const simRoiData = () => {
    const min = savedLineups?.sort((a, b) => a.simLineupRoi - b.simLineupRoi)[0]?.simLineupRoi * 100 || 0
    const max = savedLineups?.sort((a, b) => b.simLineupRoi - a.simLineupRoi)[0]?.simLineupRoi * 100 || 0
    const avg = savedLineups?.reduce((acc, curr) => acc + curr.simLineupRoi * 100, 0) / savedLineups.length || 0

    return {
      min: min ? min.toFixed(1) : '-',
      avg: avg ? avg.toFixed(1) : '-',
      max: max ? max.toFixed(1) : '-',
    }
  }

  const projFPData = () => {
    const min = savedLineups?.sort((a, b) => a.simAverageFantasyPoints - b.simAverageFantasyPoints)[0]?.simAverageFantasyPoints || 0
    const max = savedLineups?.sort((a, b) => b.simAverageFantasyPoints - a.simAverageFantasyPoints)[0]?.simAverageFantasyPoints || 0
    const avg = savedLineups?.reduce((acc, curr) => acc + curr.simAverageFantasyPoints, 0) / savedLineups.length || 0

    return {
      min: min ? min.toFixed(2) : '-',
      avg: avg ? avg.toFixed(2) : '-',
      max: max ? max.toFixed(2) : '-',
    }
  }
  const data = [
    {
      label: 'Sim ROI',
      ...simRoiData()
    },
    {
      label: 'Proj. FP',
      ...projFPData()
    },
  ]

  return (
    <Stack direction='row' alignItems='center' columnGap={1}>
      <Stack direction='row' alignItems={'center'} columnGap={1}>
        <Typography variant='caption' sx={{ fontSize: '13.33px', fontWeight: 900 }}>
          Lineup Portfolio Overview
        </Typography>
        <Tooltip title={toolTipText} placement='top' >
          <InfoOutlined fontSize='small' />
        </Tooltip>
      </Stack>
      <Stack direction='row' alignItems={'center'} columnGap={1}>
        {
          data.map((d, i) => (
            <DataPoint key={i} label={d.label} max={d.max.toString()} min={d.min.toString()} avg={d.avg.toString()} />
          ))
        }
      </Stack>
    </Stack>
  )
}

export default LineupPortfolioOverview